import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Globe, Server, ClipboardList, Sparkles, Cloud, Bot, ArrowRight, Play, X } from 'lucide-react';
import { siteData } from '../data/mock';

const iconMap = {
  Globe,
  Server,
  ClipboardList,
  Sparkles,
  Cloud,
  Bot,
};

const VideoModal = ({ isOpen, onClose, video }) => {
  if (!isOpen || !video) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.9 }}
          transition={{ duration: 0.3 }}
          className="relative w-full max-w-4xl bg-[var(--text-primary)] rounded-2xl overflow-hidden shadow-2xl"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-white/10">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-[var(--accent-primary)] flex items-center justify-center">
                <Bot className="w-5 h-5 text-[var(--text-primary)]" />
              </div>
              <div>
                <h3 className="text-white font-semibold">{video.title}</h3>
                <p className="text-white/60 text-sm">{video.subtitle}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20 transition-colors"
            >
              <X className="w-5 h-5 text-white" />
            </button>
          </div>

          {/* Video */}
          <div className="aspect-video bg-black">
            <video
              src={video.url}
              controls
              autoPlay
              className="w-full h-full"
            >
              Your browser does not support the video tag.
            </video>
          </div>

          {/* CTA */}
          <div className="p-4 bg-white/5">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <p className="text-white/70 text-sm">
                Ready to transform your business like this?
              </p>
              <a
                href="#contact"
                onClick={onClose}
                className="btn-primary text-sm px-6 py-2.5"
              >
                Book a Demo
              </a>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

const ProblemSolution = () => {
  const { painPoints, solutions } = siteData;
  const [isVideoOpen, setIsVideoOpen] = useState(false);
  const [activeVideo, setActiveVideo] = useState(null);

  const videos = {
    "Intelligent Automation": {
      url: "https://customer-assets.emergentagent.com/job_aifirst-sme/artifacts/zize03qi_Meet_Your_New_Digital_Employee.mp4",
      title: "Meet Your New Digital Employee",
      subtitle: "See AI Agents in action",
      buttonText: "What is an AI Agent?",
    },
    "AI-Powered Websites": {
      url: "https://customer-assets.emergentagent.com/job_aifirst-sme/artifacts/yxhsrxfq_HappyVan__A_Digital_Moving_Solution.mp4",
      title: "HappyVan: A Digital Moving Solution",
      subtitle: "See a real client website in action",
      buttonText: "See It In Action",
    },
    "Cloud Migration": {
      url: "https://customer-assets.emergentagent.com/job_aifirst-sme/artifacts/0hx6wadk_Modernize_to_Soar.mp4",
      title: "Modernise to Soar",
      subtitle: "See how Google Workspace transforms businesses",
      buttonText: "Why Cloud?",
    },
  };

  const openVideo = (solutionTitle) => {
    setActiveVideo(videos[solutionTitle]);
    setIsVideoOpen(true);
  };

  return (
    <section className="py-20 md:py-32 bg-[var(--bg-section)]">
      <div className="container">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center max-w-3xl mx-auto mb-16"
        >
          <span className="inline-block px-4 py-1.5 rounded-full bg-[var(--accent-wash)] text-[var(--accent-text)] text-sm font-medium mb-4">
            The Problem & Solution
          </span>
          <h2 className="heading-2 mb-4">
            Stuck with Aging Tech?<br />We've Got You Covered.
          </h2>
          <p className="body-large">
            SMEs face unique challenges in the digital age. We turn those pain points into competitive advantages.
          </p>
        </motion.div>

        {/* Pain Points */}
        <div className="grid md:grid-cols-3 gap-6 mb-16">
          {painPoints.map((point, index) => {
            const IconComponent = iconMap[point.icon];
            return (
              <motion.div
                key={point.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="card bg-white/80 backdrop-blur-sm group hover:border-red-200"
              >
                <div className="w-12 h-12 rounded-xl bg-red-50 flex items-center justify-center mb-4 group-hover:bg-red-100 transition-colors">
                  <IconComponent className="w-6 h-6 text-red-500" />
                </div>
                <h3 className="heading-3 mb-2">{point.title}</h3>
                <p className="text-[var(--text-secondary)] leading-relaxed">
                  {point.description}
                </p>
              </motion.div>
            );
          })}
        </div>

        {/* Arrow transition */}
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.4 }}
          className="flex justify-center mb-16"
        >
          <div className="flex items-center gap-4 px-6 py-3 rounded-full bg-white border border-black/10">
            <span className="text-[var(--text-secondary)] font-medium">Transform into</span>
            <ArrowRight className="w-5 h-5 text-[var(--accent-strong)]" />
          </div>
        </motion.div>

        {/* Solutions */}
        <div className="grid md:grid-cols-3 gap-6">
          {solutions.map((solution, index) => {
            const IconComponent = iconMap[solution.icon];
            const hasVideo = videos[solution.title];
            
            return (
              <motion.div
                key={solution.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="card bg-white/80 backdrop-blur-sm group hover:border-[var(--accent-primary)]"
              >
                <div className="w-12 h-12 rounded-xl bg-[var(--accent-wash)] flex items-center justify-center mb-4 group-hover:bg-[var(--accent-primary)]/20 transition-colors">
                  <IconComponent className="w-6 h-6 text-[var(--accent-text)]" />
                </div>
                <h3 className="heading-3 mb-2">{solution.title}</h3>
                <p className="text-[var(--text-secondary)] leading-relaxed mb-4">
                  {solution.description}
                </p>
                
                {/* Video button for solutions with videos */}
                {hasVideo && (
                  <button
                    onClick={() => openVideo(solution.title)}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[var(--text-primary)] text-white text-sm font-medium hover:bg-[var(--text-primary)]/90 transition-colors group/btn"
                  >
                    <div className="w-6 h-6 rounded-full bg-[var(--accent-primary)] flex items-center justify-center group-hover/btn:scale-110 transition-transform">
                      <Play className="w-3 h-3 text-[var(--text-primary)] ml-0.5" fill="currentColor" />
                    </div>
                    {hasVideo.buttonText}
                  </button>
                )}
              </motion.div>
            );
          })}
        </div>
      </div>

      {/* Video Modal */}
      <VideoModal
        isOpen={isVideoOpen}
        onClose={() => setIsVideoOpen(false)}
        video={activeVideo}
      />
    </section>
  );
};

export default ProblemSolution;
